<template>
  <d-container fluid class="main-content-container px-4">
    <!-- Page Header -->
    <d-row no-gutters class="page-header py-4">
      <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">Dokumenty elektroniczne</span>
        <h3 class="page-title">Lista dokumentów elektronicznych</h3>
      </d-col>
      <d-col col sm="6" class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button theme="success"
                  @click.prevent="$router.push('/dokumenty-elektroniczne/nowy')">
          <i class="material-icons">add</i>
          Dodaj dokument
        </d-button>
      </d-col>
    </d-row>
    <d-row
      v-if="loaded"
      style="width: 100%;"
      class="d-flex ml-0 mb-3 page-filters">
      <validation-observer
        ref="documents-filters-form"
        tag="form"
        style="width: 100%;"
        class="d-flex">
        <d-input
          placeholder="Podaj nazwę dokumentu"
          v-model="filters.search" />
        <v-select
          style="width: 360px;"
          class="ml-2"
          :options="categories"
          :reduce="category => category.id"
          v-model="filters.category_id"
          placeholder="Lista kategorii"
          :searchable="false"
          label="name">
          <template slot="option" slot-scope="option">
            <div class="d-center">
              {{ option.name ? option.name : '' }}
            </div>
          </template>
          <template slot="selected-option" slot-scope="option">
            <div class="selected d-center">
              {{ option.name ? option.name : '' }}
            </div>
          </template>
        </v-select>
        <d-button
          theme="info"
          outline
          class="text-nowrap ml-2"
          @click.prevent="validateFiltersForm">Szukaj</d-button>

        <d-button
          theme="danger"
          outline
          class="text-nowrap ml-2"
          @click.prevent="clearFilters">Resetuj filtry</d-button>
      </validation-observer>
    </d-row>
    <d-card style="width: 100%;">
      <table class="table mt-0 mb-0 full-width">
        <thead class="bg-light">
        <tr>
          <th scope="col" class="border-0">Id dokumentu</th>
          <th scope="col" class="border-0">Nazwa dokumentu</th>
          <th scope="col" class="border-0">Nazwa kategorii</th>
          <th scope="col" class="border-0">Plik do pobrania</th>
          <th scope="col" class="border-0">Akcje</th>
        </tr>
        </thead>
        <tbody v-if="loaded">
        <tr
          v-for="(document, index) of documents"
          :key="'user-' + index">
          <td class="align-middle">
            {{ document.id }}
          </td>
          <td class="align-middle">
            {{ document.name }}
          </td>
          <td class="align-middle">
            {{ document.category_name }}
          </td>
          <td class="align-middle">
            <d-button
              theme="info"
              class="mr-2"
              outline
              :disabled="downloading !== false"
              @click.prevent="download(document.id, document.filename)">
              <template v-if="downloading !== document.filename"><i class="material-icons">cloud_download</i><span class="ml-2">Pobierz</span></template>
              <template v-else>
                <div class="spinner-border spinner-border-sm" role="status"></div>
              </template>
            </d-button>
          </td>
          <td
            class="align-middle"
            style="width: 200px;">
            <d-button
              theme="primary"
              @click.prevent="$router.push('/dokumenty-elektroniczne/edycja/' + document.id)"
              class="mr-2"
              title="Edytuj">
              <i class="material-icons">edit</i>
            </d-button>
            <d-button
              theme="danger"
              :disabled="removeInProgress"
              @click.prevent="removeDocument(document.id)"
              title="Usuń">
              <i class="material-icons">delete</i>
            </d-button>
          </td>
        </tr>
        <tr v-if="documents.length === 0">
          <td
            colspan="5"
            class="text-center">
            Brak dokumentów do wyświetlenia&hellip;
          </td>
        </tr>
        </tbody>
      </table>

      <div v-if="!loaded" class="pb-3 pt-3 text-center">
        Trwa ładowanie danych&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show
        theme="warning">
        Wczytywanie danych nie powiodło się. <a href="javascript:window.location.reload();" class="alert-link">Odśwież stronę</a> aby spróbować ponownie.
      </d-alert>
    </d-card>

    <pagination :meta-data="paginationMetaData" />
  </d-container>
</template>

<script>
import debounce from 'lodash.debounce';
import FormUtils from '../../utils/FormUtils.js';
import HasFilters from '../../mixins/HasFilters.vue';
import HasPagination from '../../mixins/HasPagination.vue';
import ListUtils from '../../utils/ListUtils.js';
import Pagination from '../../components/common/Pagination.vue';
import Vue from 'vue';
import axios from 'axios'
import api from '@/api'

export default {
  name: 'EDocuments',
  mixins: [
    HasFilters,
    HasPagination
  ],
  components: {
    'pagination': Pagination
  },
  data() {
    return {
      documents: [],
      categories: [],
      downloading: false,
      loaded: false,
      loadError: false,
      removeInProgress: false,
      filters: {
        search: '',
        category_id: ''
      }
    };
  },
  mounted () {
    this.loadDocument();
    this.loadCategoriesListData();
  },
  methods: {
    loadCategoriesListData () {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/edocuments/category/items',
        outputKey: 'categories',
      });
    },
    debouncedLoadFilteredData: debounce(function () {
      this.$bus.$emit('view-filters-save');
      this.loadDocument();
    }, 250),
    loadDocument () {
      let where = {
        search: this.filters.search
      };
      if (this.filters.category_id !== null && this.filters.category_id) {
        where.category_id = this.filters.category_id;
      }
      ListUtils.loadItemsData(this, {
        endpoint: '/edocuments/items?page=' + this.currentPage,
        listField: 'documents',
        pagination: true,
        params: {
          where
        }
      });
    },
    clearFilters () {
      this.loaded = false;
      this.loadError = false;
      this.filters = {
        search: '',
        category_id: ''
      };

      this.loadDocument();
      this.$bus.$emit('view-filters-reset');
    },
    validateFiltersForm () {
      this.$bus.$emit('pagination-reset');
      FormUtils.validate(this.$refs['documents-filters-form'], this.debouncedLoadFilteredData);
    },
    download (docID, name) {
      this.downloading = true;
      let filename = name;
      axios({
        url: `${api}edocuments/get-file/` + docID,
        method: 'POST',
        responseType: 'blob'
      }).then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        this.downloading = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: 'Wystąpił błąd podczas pobierania',
          html: 'Spróbuj ponownie',
          type: 'warning',
          confirmButtonText: 'OK',
        });
        this.downloading = false;
      });
    },
    removeDocument (docID) {
      this.removeInProgress = true;

      ListUtils.removeItem(this, {
        endpointBase: '/edocuments/delete/',
        id: docID,
        successTitle: 'Usunięto dokument elektroniczny',
        successText: 'Wybrany dokument elektroniczny został usunięty',
        confirmTitle: 'Czy na pewno chcesz usunąć ten dokument elektroniczny?',
        confirmText: 'Tej operacji nie można cofnąć',
        errorTitle: 'Wystąpił błąd',
        errorText: 'Usuwanie dokumentu elektronicznego nie powiodło się. Spróbuj ponownie.',
        successAction: () => {
          this.removeInProgress = false;
          this.loadDocument();
        },
        cancelAction: () => {
          this.removeInProgress = false;
        }
      });
    }
  }
};
</script>

